// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { User } from 'src/app/shared/models/user/user.model';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-upload-company-doc-dialog',
  templateUrl: './upload-company-doc-dialog.component.html',
  styleUrls: ['./upload-company-doc-dialog.component.scss']
})
export class UploadCompanyDocDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  showBcc = false;
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.upload'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-file-upload',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;
  previousValue: any = {};
  savePath: string;
  parent: string;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<UploadCompanyDocDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public confirmDialog: MatDialog,
    public configService: ConfigService,

    private cookieService: CookieService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private authService: AuthService,
    private helperService: HelperService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser.setData(res)));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    this.initForm();

    this.savePath = this.data.path;
    if (this.savePath && this.savePath !== 'root' && this.savePath.endsWith('/')) {
      this.savePath = this.data.path.slice(0, -1);
    }

    this.parent = this.data.parent;
    if (!this.parent) {
      this.parent = 'root';
    }

    // Stop loading dialog
    setTimeout(() => {
      this.dialogIsLoading = false;
    }, 500);
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'name',
            templateOptions: {
              label: this.translateService.instant('companies.detail.documentsTab.name'),
              placeholder: this.translateService.instant('companies.detail.documentsTab.name'),
              required: true,
              maxLength: 20,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'description',
            templateOptions: {
              rows: 4,
              label: this.translateService.instant('interventions.fields.description'),
              placeholder: this.translateService.instant('interventions.fields.description'),
              required: false,
              maxLength: 5000,
            },
            expressionProperties: {
              'templateOptions.description': '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            }
          },
          {
            className: "col-12",
            type: "file-uploader",
            key: "files",
            templateOptions: {
              required: false,
              allowedFilesNumber: 1,
              allowedFilesExtensions: this.configService.allowedDocumentExtensions,
              bindLabel: "originalName",
            }
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    if (this.model && !this.model.name) {
      this.toastrService.error("Devi scegliere un nome per il file prima di procedere");
      return;
    }
    if (this.model && (!this.model.files || (this.model.files && !this.model.files.length))) {
      this.toastrService.error("Devi caricare un file prima di procedere");
      return;
    }

    this.dialogIsLoading = true;

    const newFilesData = {
      ...this.model,
      savePath: this.savePath,
    };

    this.savePath = this.savePath.replace(/\//g, '\\');

    this.subscriptions.push(this.companiesService.createDoc(this.data.companyId, newFilesData)
      .subscribe(
        res => {
          if (res.status) {
            const updatedData = {
              ...res,
              parent: this.parent,
            };
            this.dialogIsLoading = false;
            this.dialogRef.close(updatedData);
          }
        },
        err => {
          this.helperService.handleError(err);
          this.dialogIsLoading = false;
        }
      ));
  }
}
